import React, { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Check,
  CaretRight,
  X as XIcon,
  Microphone,
  User as UserIcon,
  Target,
} from "@phosphor-icons/react";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";

/**
 * SidebarAktivAlap — discreet global Foundation indicator.
 *
 *   Sidebar trigger (desktop + mobile menu):
 *     • Single line: glowing purple dot + "Aktív alap".
 *     • Hover (desktop only) shows a tiny tooltip — no popover, no panel.
 *     • Click opens a drawer (right-side on desktop, bottom-sheet on mobile).
 *
 *   Drawer / bottom-sheet body:
 *     • Title "Aktív alap" + short helper.
 *     • Three compact selects (Márkahang / Avatar / Ajánlat). When only one
 *       saved version exists, the field renders as a passive status line.
 *     • Auto-save on change → small "Mentve ✓" pulse, no explicit save btn.
 *     • Secondary link "Új alap létrehozása" at the bottom.
 *
 *   Persistence: the active id per bucket is stored in localStorage
 *     (mex_active_alap). This is a UI hint that agents may read later —
 *     no backend write is required for the swap to feel instant.
 */

const STORAGE_KEY = "mex_active_alap";
const BASES = [
  { key: "voice",  label: "Márkahang", route: "/agents/voice",  icon: Microphone },
  { key: "client", label: "Avatar",    route: "/agents/client", icon: UserIcon },
  { key: "offer",  label: "Ajánlat",   route: "/agents/offer",  icon: Target },
];

function loadActiveLocal() {
  try {
    const v = JSON.parse(localStorage.getItem(STORAGE_KEY) || "{}");
    return v && typeof v === "object" ? v : {};
  } catch {
    return {};
  }
}
function saveActiveLocal(next) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch {}
}

function useIsMobile() {
  const [mobile, setMobile] = useState(() =>
    typeof window !== "undefined" ? window.innerWidth < 768 : false
  );
  useEffect(() => {
    const onResize = () => setMobile(window.innerWidth < 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  return mobile;
}

export default function SidebarAktivAlap() {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const { previewAsTester } = useAuth();

  const [open, setOpen] = useState(false);
  const [data, setData] = useState({ voice: [], client: [], offer: [] });
  const [active, setActive] = useState(() => loadActiveLocal());
  const [savedFlash, setSavedFlash] = useState(false);

  // Server-first hydration: fetch the truth from backend on mount + focus.
  // localStorage is only a fallback for offline / pre-fetch flash.
  useEffect(() => {
    if (previewAsTester) return;
    let alive = true;
    const hydrate = async () => {
      try {
        const res = await api.get("/foundation/active-alap");
        if (!alive) return;
        const server = {
          voice: res.data?.voice || null,
          client: res.data?.client || null,
          offer: res.data?.offer || null,
        };
        setActive(server);
        saveActiveLocal(server); // mirror to LS so next paint is instant
      } catch {
        /* keep local fallback */
      }
    };
    hydrate();
    const onFocus = () => hydrate();
    window.addEventListener("focus", onFocus);
    return () => {
      alive = false;
      window.removeEventListener("focus", onFocus);
    };
  }, [previewAsTester]);

  // Load identity buckets. Re-fetch on focus so the drawer reflects new
  // saves without a manual refresh.
  // Tester preview módban NINCS load — az admin Identitás adatai NEM
  // jelenhetnek meg a preview-ban.
  useEffect(() => {
    if (previewAsTester) {
      setData({ voice: [], client: [], offer: [] });
      return;
    }
    let alive = true;
    const load = async () => {
      try {
        const res = await api.get("/identity");
        if (!alive) return;
        setData({
          voice: Array.isArray(res.data?.voice) ? res.data.voice : [],
          client: Array.isArray(res.data?.client) ? res.data.client : [],
          offer: Array.isArray(res.data?.offer) ? res.data.offer : [],
        });
      } catch {
        if (alive) setData({ voice: [], client: [], offer: [] });
      }
    };
    load();
    const onFocus = () => load();
    window.addEventListener("focus", onFocus);
    return () => {
      alive = false;
      window.removeEventListener("focus", onFocus);
    };
  }, [previewAsTester]);

  const handleSelect = useCallback((key, id) => {
    setActive((prev) => {
      const next = { ...prev, [key]: id };
      saveActiveLocal(next); // optimistic mirror
      // Fire-and-forget PUT to server. Server validates against the user's
      // saved_blueprints and echoes back the cleaned truth.
      api.put("/foundation/active-alap", next).catch(() => { /* server-side cleanup falls back next hydrate */ });
      return next;
    });
    setSavedFlash(true);
    setTimeout(() => setSavedFlash(false), 1600);
  }, []);

  // Activation gate: az Aktív alap gomb akkor látszik, ha a usernek
  // van LEGALÁBB 1 mentett Foundation snapshotja (bármilyen típusból).
  // Így a sidebar panel akkor is elérhető, ha pl. törölt valamit a
  // Tudástárból (és csak részleges Foundation-je van), vagy ha még csak
  // a Márkahangot mentette le.  A drawer-ben utána látja, melyik
  // típusból kell még készíteni.
  const totalSaved =
    (data.voice?.length || 0) +
    (data.client?.length || 0) +
    (data.offer?.length || 0);
  const isReady = totalSaved > 0;

  if (!isReady) return null;

  return (
    <>
      <SidebarTrigger onOpen={() => setOpen(true)} />
      {typeof document !== "undefined" && createPortal(
        <AnimatePresence>
          {open && (
            <Surface
              isMobile={isMobile}
              onClose={() => setOpen(false)}
              data={data}
              active={active}
              onSelect={handleSelect}
              savedFlash={savedFlash}
              onCreate={(route) => {
                setOpen(false);
                navigate(route);
              }}
            />
          )}
        </AnimatePresence>,
        document.body
      )}
    </>
  );
}

/* ──────────── Sidebar trigger ──────────── */

function SidebarTrigger({ onOpen }) {
  return (
    <div
      className="relative px-3 pt-3 pb-2"
      style={{ borderTop: "1px solid rgba(255,255,255,0.06)" }}
    >
      <button
        type="button"
        onClick={onOpen}
        data-testid="sidebar-aktiv-alap"
        className="w-full flex items-center gap-2 rounded-md transition"
        style={{
          padding: "8px 10px",
          background: "transparent",
          border: "1px solid transparent",
          color: "#a1a1a1",
          fontFamily: "inherit",
          cursor: "pointer",
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = "rgba(255,255,255,0.025)";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = "transparent";
        }}
      >
        <span
          aria-hidden="true"
          style={{
            width: 7,
            height: 7,
            borderRadius: "50%",
            background: "#7c6cf2",
            boxShadow: "0 0 8px rgba(124,108,242,0.8)",
            flexShrink: 0,
          }}
        />
        <span style={{ fontSize: 12, fontWeight: 500 }}>Aktív alap</span>
      </button>
    </div>
  );
}

/* ──────────── Surface (drawer / bottom-sheet) ──────────── */

function Surface({ isMobile, onClose, data, active, onSelect, savedFlash, onCreate }) {
  // Locks body scroll while the surface is open.
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, []);

  // ESC closes the surface (keyboard accessibility).
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const isMobileLayout = isMobile;

  // Frosted glass support detection. If the browser supports backdrop-filter,
  // we apply the translucent gradient + blur so the content behind blurs into
  // a soft hue. Otherwise we fall back to a fully opaque gradient — never an
  // unblurred low-alpha background that could leak readable text.
  const supportsBlur = React.useMemo(() => {
    if (typeof window === "undefined" || !window.CSS || !window.CSS.supports) {
      return false;
    }
    return (
      window.CSS.supports("backdrop-filter", "blur(1px)") ||
      window.CSS.supports("-webkit-backdrop-filter", "blur(1px)")
    );
  }, []);

  const panelBackground = supportsBlur
    ? "linear-gradient(168deg, rgba(40,35,92,0.80) 0%, rgba(28,24,62,0.85) 45%, rgba(22,19,47,0.90) 100%)"
    : "linear-gradient(168deg, #28235c 0%, #1c183e 45%, #16132f 100%)";

  return (
    <>
      {/* Backdrop — gyengéd, on-hue dim, click to close */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.2 }}
        onClick={onClose}
        data-testid="sidebar-aktiv-alap-backdrop"
        style={{
          position: "fixed",
          inset: 0,
          backgroundColor: "rgba(22,19,47,0.36)",
          zIndex: 9998,
        }}
      />

      {/* Panel — frosted glass: translucent brand-purple + heavy blur */}
      <motion.div
        initial={isMobileLayout ? { y: "100%" } : { x: "100%" }}
        animate={isMobileLayout ? { y: 0 } : { x: 0 }}
        exit={isMobileLayout ? { y: "100%" } : { x: "100%" }}
        transition={{ type: "tween", duration: 0.28, ease: "easeOut" }}
        data-testid="sidebar-aktiv-alap-drawer"
        style={{
          position: "fixed",
          background: panelBackground,
          backdropFilter: supportsBlur ? "blur(30px) saturate(150%)" : undefined,
          WebkitBackdropFilter: supportsBlur ? "blur(30px) saturate(150%)" : undefined,
          opacity: 1,
          zIndex: 9999,
          display: "flex",
          flexDirection: "column",
          color: "#e9e7f5",
          overflow: "hidden",
          ...(isMobileLayout
            ? {
                left: 0,
                right: 0,
                bottom: 0,
                maxHeight: "55vh",
                minHeight: "45vh",
                borderTopLeftRadius: 22,
                borderTopRightRadius: 22,
                borderTop: "1px solid rgba(94,80,247,0.38)",
                boxShadow:
                  "0 -20px 54px -16px rgba(0,0,0,0.38), 0 -8px 42px -12px rgba(94,80,247,0.20), inset 0 1px 0 rgba(255,255,255,0.05)",
              }
            : {
                top: 0,
                right: 0,
                bottom: 0,
                width: 340,
                borderTopLeftRadius: 22,
                borderBottomLeftRadius: 22,
                borderLeft: "1px solid rgba(94,80,247,0.38)",
                boxShadow:
                  "-20px 0 54px -16px rgba(0,0,0,0.38), -8px 0 42px -12px rgba(94,80,247,0.20), inset 0 1px 0 rgba(255,255,255,0.05)",
              }),
        }}
      >
        {/* Atmoszférikus lila derengés — Foundation screen-szerű, a teljes
            drawer-t lefedi, nem interaktív. A frosted háttér FELETT ül. */}
        <div
          aria-hidden="true"
          style={{
            position: "absolute",
            inset: 0,
            pointerEvents: "none",
            background:
              "radial-gradient(130% 80% at 50% -8%, rgba(94,80,247,0.22), rgba(94,80,247,0) 62%)",
            zIndex: 0,
          }}
        />

        {/* Mobile drag handle */}
        {isMobileLayout && (
          <div className="flex justify-center pt-2.5" style={{ position: "relative", zIndex: 1 }}>
            <span
              aria-hidden="true"
              style={{
                width: 36,
                height: 4,
                borderRadius: 999,
                background: "rgba(255,255,255,0.14)",
              }}
            />
          </div>
        )}

        {/* Header */}
        <div
          className="flex items-start justify-between"
          style={{
            padding: isMobileLayout ? "16px 28px 10px" : "28px 28px 10px",
            position: "relative",
            zIndex: 1,
          }}
        >
          <div>
            <h2
              className="font-heading"
              style={{
                fontSize: 19,
                fontWeight: 700,
                color: "#ffffff",
                letterSpacing: "-0.01em",
                lineHeight: 1.2,
              }}
            >
              Aktív alap
            </h2>
            <p
              style={{
                fontSize: 12.5,
                color: "#aaa4c8",
                marginTop: 4,
                lineHeight: 1.5,
              }}
            >
              Ezekből dolgoznak az ügynökök.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="sidebar-aktiv-alap-close"
            aria-label="Bezárás"
            className="transition"
            style={{
              width: 32,
              height: 32,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: 10,
              background: "rgba(94,80,247,0.12)",
              border: "1px solid rgba(94,80,247,0.22)",
              color: "#b8b2d4",
              cursor: "pointer",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = "rgba(94,80,247,0.22)";
              e.currentTarget.style.color = "#ffffff";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "rgba(94,80,247,0.12)";
              e.currentTarget.style.color = "#b8b2d4";
            }}
          >
            <XIcon size={14} weight="bold" />
          </button>
        </div>

        {/* Fields */}
        <div
          className="flex-1 overflow-y-auto"
          style={{
            padding: "30px 28px 18px",
            position: "relative",
            zIndex: 1,
          }}
        >
          {BASES.map((b, idx) => (
            <div key={b.key} style={{ marginBottom: idx === BASES.length - 1 ? 0 : 22 }}>
              <BaseField
                base={b}
                bucket={data[b.key] || []}
                activeId={active[b.key]}
                onPick={(id) => onSelect(b.key, id)}
                onCreate={() => onCreate(b.route)}
                isMobile={isMobileLayout}
              />
            </div>
          ))}
        </div>

        {/* Footer */}
        <div
          style={{
            padding: "14px 28px 22px",
            borderTop: "1px solid rgba(94,80,247,0.16)",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 12,
            position: "relative",
            zIndex: 1,
          }}
        >
          <SavedFlash visible={savedFlash} />
          <button
            type="button"
            onClick={() => onCreate("/identity")}
            data-testid="sidebar-aktiv-alap-create"
            className="transition"
            style={{
              fontSize: 12.5,
              color: "#a99dff",
              background: "transparent",
              border: "none",
              cursor: "pointer",
              padding: "6px 10px",
              borderRadius: 8,
              fontFamily: "inherit",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = "rgba(94,80,247,0.14)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "transparent";
            }}
          >
            Új alap létrehozása
          </button>
        </div>
      </motion.div>
    </>
  );
}

function SavedFlash({ visible }) {
  return (
    <AnimatePresence>
      {visible && (
        <motion.span
          initial={{ opacity: 0, y: 4 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 4 }}
          transition={{ duration: 0.18 }}
          data-testid="sidebar-aktiv-alap-saved"
          style={{
            fontSize: 11.5,
            color: "#4cb585",
            display: "inline-flex",
            alignItems: "center",
            gap: 4,
            fontWeight: 500,
          }}
        >
          <Check size={11} weight="bold" /> Mentve
        </motion.span>
      )}
    </AnimatePresence>
  );
}

/* ──────────── Per-base field (select w/ mobile picker) ──────────── */

function BaseField({ base, bucket, activeId, onPick, onCreate, isMobile }) {
  const [pickerOpen, setPickerOpen] = useState(false);

  const resolvedId = (activeId && bucket.some((b) => b.id === activeId))
    ? activeId
    : bucket[0]?.id || "";
  const current = bucket.find((it) => it.id === resolvedId);
  const empty = bucket.length === 0;
  const single = bucket.length === 1;

  const handleChange = (id) => onPick(id);

  const Icon = base.icon;
  const [hovered, setHovered] = React.useState(false);
  const swappable = !empty && !single;

  // Field outer style — varies by state and hover. The swappable variant
  // resting state has a brighter border (like Foundation cards), and on
  // hover lights up into a full Brand Építő-style glow.
  const restingBg = "rgba(94,80,247,0.10)";
  const restingBorder = swappable ? "rgba(94,80,247,0.30)" : "rgba(94,80,247,0.20)";
  const hoverBg = "rgba(94,80,247,0.16)";
  const hoverBorder = "rgba(94,80,247,0.46)";
  const hoverShadow = "0 0 26px -4px rgba(94,80,247,0.38)";

  const useHover = swappable && hovered;
  const fieldBoxStyle = {
    display: "flex",
    alignItems: "center",
    gap: 12,
    background: useHover ? hoverBg : restingBg,
    border: `1px solid ${useHover ? hoverBorder : restingBorder}`,
    borderRadius: 16,
    padding: "13px 14px",
    boxShadow: useHover ? hoverShadow : "none",
    transition: "background 0.18s, border-color 0.18s, box-shadow 0.18s",
    fontFamily: "inherit",
    width: "100%",
    textAlign: "left",
  };

  const tileBg = useHover ? "rgba(94,80,247,0.30)" : "rgba(94,80,247,0.15)";
  const tileIconColor = useHover ? "#c8c1ff" : "#a99dff";
  const renderTile = () => (
    <span
      aria-hidden="true"
      style={{
        width: 32,
        height: 32,
        borderRadius: 10,
        background: tileBg,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
        transition: "background 0.18s",
      }}
    >
      <Icon size={16} weight="bold" color={tileIconColor} />
    </span>
  );

  const renderName = (text, fallbackColor) => (
    <span
      className="truncate"
      style={{
        fontSize: 13.5,
        color: fallbackColor || "#e9e7f5",
        flex: 1,
        minWidth: 0,
        overflow: "hidden",
        whiteSpace: "nowrap",
        textOverflow: "ellipsis",
      }}
    >
      {text}
    </span>
  );

  return (
    <div data-testid={`sidebar-aktiv-alap-field-${base.key}`}>
      <label
        style={{
          fontSize: 10.5,
          color: "#8d86ad",
          letterSpacing: "1.1px",
          textTransform: "uppercase",
          fontWeight: 700,
          display: "block",
          marginBottom: 9,
        }}
      >
        {base.label}
      </label>

      {empty ? (
        <button
          type="button"
          onClick={onCreate}
          style={{ ...fieldBoxStyle, cursor: "pointer" }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = hoverBg;
            e.currentTarget.style.borderColor = hoverBorder;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = restingBg;
            e.currentTarget.style.borderColor = restingBorder;
          }}
        >
          {renderTile()}
          {renderName("Még nincs kész", "#aaa4c8")}
          <span style={{ fontSize: 11.5, color: "#a99dff", flexShrink: 0 }}>
            Létrehozom →
          </span>
        </button>
      ) : single ? (
        // Single saved version — passive status line, no chevron, no glow.
        <div
          style={fieldBoxStyle}
          data-testid={`sidebar-aktiv-alap-${base.key}-single`}
        >
          {renderTile()}
          {renderName(current?.name?.trim() || "—")}
        </div>
      ) : isMobile ? (
        <>
          <button
            type="button"
            onClick={() => setPickerOpen(true)}
            data-testid={`sidebar-aktiv-alap-${base.key}-trigger`}
            style={{ ...fieldBoxStyle, cursor: "pointer" }}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
          >
            {renderTile()}
            {renderName(current?.name?.trim() || "—")}
            <CaretRight
              size={15}
              weight="bold"
              color="#8d86ad"
              style={{ flexShrink: 0 }}
            />
          </button>
          <AnimatePresence>
            {pickerOpen && (
              <MobilePicker
                base={base}
                bucket={bucket}
                activeId={resolvedId}
                onPick={(id) => {
                  handleChange(id);
                  setPickerOpen(false);
                }}
                onClose={() => setPickerOpen(false)}
              />
            )}
          </AnimatePresence>
        </>
      ) : (
        // Desktop swappable — visual wrapper with icon tile + chevron, and
        // an invisible native <select> stretched on top for accessibility
        // (keyboard nav, screen readers, options popover).
        <div
          style={{ position: "relative", cursor: "pointer" }}
          onMouseEnter={() => setHovered(true)}
          onMouseLeave={() => setHovered(false)}
        >
          <div style={fieldBoxStyle}>
            {renderTile()}
            {renderName(current?.name?.trim() || "—")}
            <CaretRight
              size={15}
              weight="bold"
              color="#8d86ad"
              style={{ flexShrink: 0 }}
            />
          </div>
          <select
            value={resolvedId}
            onChange={(e) => handleChange(e.target.value)}
            data-testid={`sidebar-aktiv-alap-${base.key}-select`}
            aria-label={base.label}
            style={{
              position: "absolute",
              inset: 0,
              width: "100%",
              height: "100%",
              opacity: 0,
              cursor: "pointer",
              fontFamily: "inherit",
              fontSize: 13,
              border: "none",
              background: "transparent",
            }}
          >
            {bucket.map((it) => (
              <option key={it.id} value={it.id} style={{ background: "#221d33", color: "#e9e7f5" }}>
                {it.name?.trim() || "—"}
              </option>
            ))}
          </select>
        </div>
      )}
    </div>
  );
}

/* ──────────── Mobile picker (full-screen list) ──────────── */

function MobilePicker({ base, bucket, activeId, onPick, onClose }) {
  return (
    <>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.18 }}
        onClick={onClose}
        style={{
          position: "fixed",
          inset: 0,
          background: "rgba(0,0,0,0.45)",
          zIndex: 100,
        }}
      />
      <motion.div
        initial={{ y: "100%" }}
        animate={{ y: 0 }}
        exit={{ y: "100%" }}
        transition={{ type: "tween", duration: 0.24, ease: "easeOut" }}
        data-testid={`sidebar-aktiv-alap-${base.key}-picker`}
        style={{
          position: "fixed",
          left: 0,
          right: 0,
          bottom: 0,
          background: "#13131c",
          borderTopLeftRadius: 18,
          borderTopRightRadius: 18,
          borderTop: "1px solid rgba(255,255,255,0.08)",
          zIndex: 110,
          maxHeight: "70vh",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <div className="flex justify-center pt-2.5">
          <span
            aria-hidden="true"
            style={{
              width: 36,
              height: 4,
              borderRadius: 999,
              background: "rgba(255,255,255,0.12)",
            }}
          />
        </div>
        <div
          style={{
            padding: "10px 18px 8px",
            fontSize: 13,
            fontWeight: 600,
            color: "#e8e8f0",
          }}
        >
          Válassz {base.label.toLowerCase()}t
        </div>
        <div
          className="flex-1 overflow-y-auto"
          style={{ padding: "4px 12px 18px" }}
        >
          {bucket.map((it) => {
            const isActive = it.id === activeId;
            return (
              <button
                key={it.id}
                type="button"
                onClick={() => onPick(it.id)}
                data-testid={`sidebar-aktiv-alap-${base.key}-pick-${it.id}`}
                className="w-full flex items-center justify-between rounded-md transition"
                style={{
                  padding: "0 14px",
                  height: 48,
                  background: isActive ? "rgba(94,80,247,0.10)" : "transparent",
                  border: `1px solid ${isActive ? "rgba(94,80,247,0.30)" : "transparent"}`,
                  fontSize: 14,
                  color: isActive ? "#b5acff" : "#e8e8f0",
                  fontWeight: isActive ? 500 : 400,
                  fontFamily: "inherit",
                  cursor: "pointer",
                  marginBottom: 4,
                }}
              >
                <span className="truncate" style={{ textAlign: "left", maxWidth: "85%" }}>
                  {it.name?.trim() || "—"}
                </span>
                {isActive && <Check size={14} color="#b5acff" weight="bold" />}
              </button>
            );
          })}
        </div>
      </motion.div>
    </>
  );
}
